import { useEffect, useState } from 'react'

/**
 * The four numbers that decide whether the app fills the space above the keyboard.
 *
 * Opened with `?vp=1` and invisible otherwise, because this geometry cannot be
 * measured anywhere except a real device: a desktop browser has no soft keyboard,
 * and a native shell's own diagnostics run in a different document that never
 * executes this app's code — so an expectation there matches only its own inputs.
 *
 * POLLED, not event-driven, and that is the point rather than laziness. The bug
 * this exists for is a value that fails to update, and a readout wired to the same
 * listeners as the code under test would go stale in exactly the cases worth
 * catching. It also answers a question events cannot: whether the numbers SETTLE.
 * Growing the app moves the focused field, iOS scrolls to reveal focused fields,
 * and that loop could oscillate — visible here as figures that will not sit still.
 *
 * `band` is the whole thing in one number. It is what the app leaves unpainted at
 * the bottom of its own frame, and it should be 0.
 */
export function ViewportProbe() {
  const [n, setN] = useState<Record<string, number | string>>({})

  useEffect(() => {
    const read = () => {
      const vv = window.visualViewport
      const appH = Number.parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue('--app-h') || '0',
      )
      const frame = window.innerHeight
      const top = vv?.offsetTop ?? 0
      setN({
        frame,
        vvH: vv ? Math.round(vv.height) : '—',
        top: Math.round(top),
        appH: Math.round(appH),
        band: Math.round(frame - top - appH),
        kb: document.body.classList.contains('kb-up') ? 'up' : 'down',
        // Whether THIS page can see the shell. The frame correction is gated on it,
        // and the only device confirmation we have predates that gate — so "the band
        // was gone" was measured on a build that did not check this. If it reads
        // `no` inside the app, the correction is inert and the band is back.
        shell: 'Capacitor' in window ? 'yes' : 'no',
      })
    }
    read()
    /*
     * Observed, not sampled. Polling our own geometry every 300ms was a heartbeat
     * aimed at the browser: it asked whether the box had changed instead of being
     * told. ResizeObserver fires on the change itself, and on nothing else.
     */
    const ro = new ResizeObserver(read)
    ro.observe(document.documentElement)
    const mo = new MutationObserver(read)
    mo.observe(document.body, { attributes: true, attributeFilter: ['class'] })
    const vv = window.visualViewport
    vv?.addEventListener('resize', read)
    vv?.addEventListener('scroll', read)
    return () => {
      ro.disconnect()
      mo.disconnect()
      vv?.removeEventListener('resize', read)
      vv?.removeEventListener('scroll', read)
    }
  }, [])

  return (
    <div
      style={{
        position: 'fixed',
        top: 'calc(var(--app-top, 0px) + 4px)',
        left: '4px',
        zIndex: 99999,
        font: '11px/1.5 ui-monospace, Menlo, monospace',
        background: 'rgba(0,0,0,0.82)',
        color: '#a6e3a1',
        padding: '4px 7px',
        borderRadius: '6px',
        pointerEvents: 'none',
        whiteSpace: 'pre',
      }}
    >
      {`frame ${n['frame']}  vv ${n['vvH']}  top ${n['top']}\nappH  ${n['appH']}  band ${n['band']}  kb ${n['kb']}\nshell ${n['shell']}`}
    </div>
  )
}

/** True when the URL asks for the probe. Read once; it never changes mid-session. */
export const wantsViewportProbe = (): boolean => new URLSearchParams(location.search).has('vp')
